import { useCallback, useState } from 'react'
import { RichGraphQLDataGrid } from 'hic-components'
import { DocumentQueryFilter, DocumentsQuery, DocumentsListFragment, DocumentsQueryVariables, DocumentsDocument, PaymentActivesQuery, ReferenceCustomerFragment } from 'src/api/api.generated'
import { useTenant } from 'src/app/hooks/context/context.hook'
import { GridColDef } from '@mui/x-data-grid'
import { keysOf } from 'src/utils/utils'
import { useTranslation } from 'react-i18next'
import { Grid, Button, Divider, MenuItem } from '@vapor/material'
import useFormatDate from 'src/app/hooks/current-locale/use-format-date'
import useFormatCurrency from 'src/app/hooks/current-locale/use-format-currency'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus } from '@fortawesome/pro-solid-svg-icons'
import PaidInFullDocumentForm from './paid-in-full-document-form'
import { DocumentCustomerFormSchema } from 'src/app/components/document-customer-input/document-customer-schema'
import { HICTitleSmall } from 'src/app/components/typography/title-small'
import UnpaidDocumentsReminderForm from '../unpaid-documents-reminder/unpaid-documents-reminder-form'
import { faPaperPlane } from '@fortawesome/pro-regular-svg-icons'
import { EditAction } from 'src/app/components/data-grid-action-cell-item'
import { useHicCommonDataGridProps } from 'src/app/components/hic-vapor/datagrid/datagrid'

type PaidInFullDocumentAction = 'NEW' | 'NEW_WITH_TSPAY' | 'REMINDER' | 'VIEW'

type PaidInFullDocumentsGridProps = {
  documentId: string
  propertyId: string
  cashDeskId: string
  customer: DocumentCustomerFormSchema
  paymentActives: PaymentActivesQuery | undefined
  referenceCustomer: ReferenceCustomerFragment
}

export function PaidInFullDocumentsGrid(props: PaidInFullDocumentsGridProps) {
  const { documentId, propertyId, cashDeskId, paymentActives, customer, referenceCustomer } = props
  const { id: tenantId } = useTenant()
  const { t } = useTranslation()
  const { hicCommonDataGridPremiumProps } = useHicCommonDataGridProps()

  const [action, setAction] = useState<PaidInFullDocumentAction | null>(null)
  const [selectedDocumentId, setSelectedDocumentId] = useState<string>()

  const actionHandler = (action: PaidInFullDocumentAction, id: string) => {
    setAction(action)
    setSelectedDocumentId(id)
  }

  const columns = usePaymentDocumentsColumns(actionHandler, documentId)

  const variables = useCallback(
    (params): DocumentsQueryVariables => ({
      filter: {
        tenantId: { eq: tenantId },
        propertyId: { eq: propertyId },
        // unpaid: true,
        documentType: 'PAID_IN_FULL',
        referenceDocumentId: { eq: documentId },
      },
      skip: params.pagination?.skip || 0,
      limit: params.pagination?.limit || 10,
    }),
    [documentId, tenantId, propertyId],
  )

  //#region actions

  const actionNewPaymentDocument = () => {
    setAction('NEW')
  }

  const actionNewPaymentDocumentDraft = () => {
    setAction('NEW_WITH_TSPAY')
  }
  const actionReminder = () => {
    setAction('REMINDER')
  }

  //#endregion

  return (
    <>
      <Grid item xs={12}>
        <Divider light sx={{ mb: 1 }} />
        <HICTitleSmall>{t('pms.accounting.documents.payment_documents_title')}</HICTitleSmall>
        <RichGraphQLDataGrid<DocumentsQuery, DocumentsListFragment, DocumentsQueryVariables, DocumentQueryFilter>
          {...hicCommonDataGridPremiumProps}
          columns={columns}
          query={DocumentsDocument}
          data={(response) => {
            return (
              response?.documents?.map((document) => {
                return {
                  ...document,
                }
              }) || []
            )
          }}
          pagination="standard"
          pageSize={10}
          variables={variables}
          actions={[
            {
              position: 'right',
              primary: true,
              component: (
                <Button variant="outlined" size="small" startIcon={<FontAwesomeIcon icon={faPlus} />} onClick={actionNewPaymentDocument}>
                  {t('pms.accounting.documents.new_payment_document')}
                </Button>
              ),
              mobileComponent: (
                <Button variant="outlined" size="small" startIcon={<FontAwesomeIcon icon={faPlus} />} onClick={actionNewPaymentDocument}>
                  {t('pms.accounting.documents.new_payment_document')}{' '}
                </Button>
              ),
            },
            {
              position: 'right',
              component: (
                <Button variant="outlined" size="small" startIcon={<FontAwesomeIcon icon={faPlus} />} onClick={actionNewPaymentDocumentDraft}>
                  {t('pms.accounting.documents.new_payment_document_draft')}
                </Button>
              ),
              menuItem: <MenuItem onClick={actionNewPaymentDocumentDraft}> {t('pms.accounting.documents.new_payment_document_draft')}</MenuItem>,
            },
            {
              position: 'right',
              component: (
                <Button variant="outlined" size="small" startIcon={<FontAwesomeIcon icon={faPaperPlane} />} onClick={actionReminder}>
                  {t('pms.accounting.documents.send_reminder')}
                </Button>
              ),
              menuItem: <MenuItem onClick={actionReminder}> {t('pms.accounting.documents.send_reminder')}</MenuItem>,
            },
          ]}
          autoHeight
          sx={{
            '& .MuiDataGrid-columnHeaderTitle': {
              width: '100%',
              textOverflow: 'clip',
              whiteSpace: 'break-spaces',
              lineHeight: 1,
            },
          }}
        />
      </Grid>
      {action !== null && ['NEW', 'NEW_WITH_TSPAY', 'VIEW'].includes(action) && (
        <PaidInFullDocumentForm //
          open={action !== null && ['NEW', 'NEW_WITH_TSPAY', 'VIEW'].includes(action)}
          createAsDraft={action === 'NEW_WITH_TSPAY'}
          tenantId={tenantId}
          propertyId={propertyId}
          onClose={() => {
            setAction(null)
            setSelectedDocumentId(undefined)
          }}
          paymentActives={paymentActives}
          defaultCustomer={customer}
          paidInFullDocumentId={selectedDocumentId}
          comeFromUnpaidDocumentId={documentId}
          referenceCustomer={referenceCustomer}
        />
      )}
      {action !== null && action === 'REMINDER' && (
        <UnpaidDocumentsReminderForm //
          open={action === 'REMINDER'}
          tenantId={tenantId}
          propertyId={propertyId}
          onClose={() => {
            setAction(null)
            setSelectedDocumentId(undefined)
          }}
          defaultCustomer={customer}
          comeFromUnpaidDocumentId={documentId}
          referenceCustomer={referenceCustomer}
        />
      )}
    </>
  )
}

const usePaymentDocumentsColumns = (onActionHandler: (action: PaidInFullDocumentAction, id: string) => void, documentId: string): GridColDef[] => {
  const { currency } = useTenant()
  const { t } = useTranslation()
  const formatDate = useFormatDate()
  const { formatTableCurrency } = useFormatCurrency()

  const documentsFragmentKey = keysOf<DocumentsListFragment>()

  return [
    {
      field: documentsFragmentKey('numberExtended'),
      headerName: t('pms.accounting.documents.progressive'),
      flex: 1,
    },
    {
      field: documentsFragmentKey('documentDate'),
      headerName: t('pms.accounting.documents.document_date'),
      width: 100,
      type: 'date',
      valueFormatter: ({ value }) => formatDate(value),
    },
    {
      field: documentsFragmentKey('totalAmount'),
      headerName: t('pms.accounting.documents.total_amount'),
      type: 'number',
      flex: 1,
      valueFormatter: ({ value }) => formatTableCurrency(value ?? 0, currency),
    },
    {
      field: documentsFragmentKey('documentPaymentDetails'),
      headerName: t('pms.accounting.documents.paid_in_full_quota'),
      type: 'number',
      flex: 1,
      valueGetter: (value) => {
        const row = value.row as DocumentsListFragment
        const paidInFullQuota = row.documentPaymentDetails.filter((r) => r.referenceDocumentId === documentId).reduce((t, i) => t + i.amount, 0)
        return paidInFullQuota
      },
      valueFormatter: ({ value }) => formatTableCurrency(value ?? 0, currency),
    },
    {
      field: 'action',
      type: 'actions',
      headerName: t('commons.table_actions'),
      width: 80,
      getActions: (params) => {
        const row = params.row as DocumentsListFragment
        return [
          <EditAction
            label={t('commons.show')}
            onClick={() => {
              onActionHandler('VIEW', row.id)
            }}
          />,
        ]
      },
    },
  ]
}
